import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  BackButton
} from '../../common/styles';
import {
  FormContainer,
  FormSection,
  SectionTitle
} from '../../EmployeesRepository/EmployeeForm/styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { permissionsApi } from '../../../services/api/permissionsApi';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import Modal from '../../common/Modal';
import { PERMISSION_OVERRIDE_TYPES } from '../../../constants/permissionOverrideTypes';

const EmployeeOverrideDetails = () => {
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [selectedResourceAction, setSelectedResourceAction] = useState(null);
  const [selectedPermissionId, setSelectedPermissionId] = useState(null);
  const [selectedOverrideType, setSelectedOverrideType] = useState(PERMISSION_OVERRIDE_TYPES.GRANT);
  const [overrideReason, setOverrideReason] = useState('');

  useEffect(() => {
    loadActions();
    loadResources();
    if (employeeId) {
      loadEmployeeData();
    }
  }, [employeeId]);

  const loadActions = async () => {
    try {
      setActionsLoading(true);
      const actionsData = await permissionsApi.getAllActions({ published: true });
      setActions(actionsData);
    } catch (error) {
      console.error('Error loading actions:', error);
      showAlert(error.message || 'Failed to load actions', 'error');
      setActions([]);
    } finally {
      setActionsLoading(false);
    }
  };

  const loadResources = async () => {
    try {
      setResourcesLoading(true);
      const resourcesData = await permissionsApi.getAllResources({ published: true });
      setResources(resourcesData);
    } catch (error) {
      console.error('Error loading resources:', error);
      showAlert(error.message || 'Failed to load resources', 'error');
      setResources([]);
    } finally {
      setResourcesLoading(false);
    }
  };

  const loadEmployeeData = async () => {
    try {
      setIsLoading(true);
      const data = await permissionsApi.getEmployeePermissionOverrideById(employeeId);
      setEmployee(data.employee);
      setOverrides(data.overrides || []);
    } catch (error) {
      console.error('Error loading employee permission overrides:', error);
      showAlert(error.message || 'Failed to load individual permission overrides', 'error');
      navigate('/permissions/employee-overrides');
    } finally {
      setIsLoading(false);
    }
  };

  const hasOverride = (resource, action) => {
    return overrides.some(override =>
      override.permission.resource?.id === resource.id &&
      override.permission.action?.id === action.id
    );
  };

  const getOverride = (resource, action) => {
    return overrides.find(override =>
      override.permission.resource?.id === resource.id &&
      override.permission.action?.id === action.id
    );
  };

  const getResourceActionPermissions = (resource, action) => {
    if (!resource.permissions || !Array.isArray(resource.permissions)) {
      return [];
    }
    return resource.permissions.filter(permission =>
      permission.action?.id === action.id
    );
  };

  const getPermissionCount = (resource, action) => {
    return getResourceActionPermissions(resource, action).length;
  };

  const resourceHasAction = (resource, action) => {
    return getResourceActionPermissions(resource, action).length > 0;
  };

  const handleMultiplePermissionsClick = (resource, action) => {
    setSelectedResourceAction({ resource, action });
    setSelectedPermissionId(null);
    setSelectedOverrideType(PERMISSION_OVERRIDE_TYPES.GRANT);
    setOverrideReason('');
    setPermissionsModalOpen(true);
  };

  const handlePermissionsModalClose = () => {
    setPermissionsModalOpen(false);
    setSelectedResourceAction(null);
    setSelectedPermissionId(null);
    setSelectedOverrideType(PERMISSION_OVERRIDE_TYPES.GRANT);
    setOverrideReason('');
  };

  const handlePermissionSelect = (permissionId) => {
    setSelectedPermissionId(permissionId);
  };

  const modalAvailablePermissions = useMemo(() => {
    if (!selectedResourceAction) return [];

    const resourcePermissions = getResourceActionPermissions(selectedResourceAction.resource, selectedResourceAction.action);

    // Get permission IDs that already have overrides
    const overriddenPermissionIds = overrides
      .filter(override =>
        override.permission.resource?.id === selectedResourceAction.resource.id &&
        override.permission.action?.id === selectedResourceAction.action.id
      )
      .map(override => override.permission.id);

    // Return only permissions that don't have overrides yet
    return resourcePermissions.filter(permission =>
      !overriddenPermissionIds.includes(permission.id)
    );
  }, [selectedResourceAction, resources, overrides]);

  const handlePermissionSubmit = async () => {
    if (selectedPermissionId && selectedResourceAction && employeeId) {
      try {
        const newOverride = await permissionsApi.assignEmployeePermissionOverride({
          permissionId: selectedPermissionId,
          employeeId: employeeId,
          overrideType: selectedOverrideType,
          reason: overrideReason || undefined
        });

        // Find the selected permission details
        const selectedPermission = modalAvailablePermissions.find(p => p.id === selectedPermissionId);

        if (selectedPermission) {
          // Update local state - add the new override
          const newOverrideEntry = {
            id: newOverride.id || `temp-${Date.now()}`,
            employeeId: employeeId,
            permissionId: selectedPermissionId,
            overrideType: selectedOverrideType,
            reason: overrideReason || null,
            permission: {
              id: selectedPermissionId,
              resource: {
                id: selectedResourceAction.resource.id,
                name: selectedResourceAction.resource.name
              },
              action: {
                id: selectedResourceAction.action.id,
                name: selectedResourceAction.action.name
              },
              conditions: selectedPermission.conditions || {},
              reason: selectedPermission.reason || null
            }
          };

          setOverrides(prevOverrides => [...prevOverrides, newOverrideEntry]);
        }

        showAlert('Permission override assigned successfully', 'success');
        handlePermissionsModalClose();
      } catch (error) {
        console.error('Error assigning permission override:', error);
        showAlert(error.message || 'Failed to assign permission override', 'error');
      }
    }
  };

  const togglePermission = async (resource, action) => {
    if (!employeeId) return;

    try {
      const existingOverride = getOverride(resource, action);

      if (existingOverride) {
        // Remove the override
        await permissionsApi.removeEmployeePermissionOverride({
          permissionId: existingOverride.permission.id,
          employeeId: employeeId
        });

        // Update local state - remove the override
        setOverrides(prevOverrides =>
          prevOverrides.filter(override => override.id !== existingOverride.id)
        );

        showAlert('Permission override removed', 'success');
      } else {
        // Add override - check available permissions
        const availablePermissions = getResourceActionPermissions(resource, action);

        if (availablePermissions.length === 1) {
          // Single permission - show modal to select Grant/Deny
          setSelectedResourceAction({ resource, action });
          setSelectedPermissionId(availablePermissions[0].id);
          setSelectedOverrideType(PERMISSION_OVERRIDE_TYPES.GRANT);
          setOverrideReason('');
          setPermissionsModalOpen(true);
        } else if (availablePermissions.length > 1) {
          // Multiple permissions - show modal to select which one
          handleMultiplePermissionsClick(resource, action);
        } else {
          showAlert(`No permissions available for ${action.name} on ${resource.name}`, 'warning');
        }
      }
    } catch (error) {
      console.error('Error toggling permission:', error);
      showAlert(error.message || 'Failed to toggle permission', 'error');
    }
  };

  const getOverrideTypeColor = (overrideType) => {
    switch (overrideType) {
      case PERMISSION_OVERRIDE_TYPES.GRANT:
        return 'success';
      case PERMISSION_OVERRIDE_TYPES.DENY:
        return 'error';
      default:
        return 'default';
    }
  };

  const getOverrideTypeIcon = (overrideType) => {
    switch (overrideType) {
      case PERMISSION_OVERRIDE_TYPES.GRANT:
        return <CheckCircleIcon fontSize="small" />;
      case PERMISSION_OVERRIDE_TYPES.DENY:
        return <DoNotDisturbOnIcon fontSize="small" />;
      default:
        return <CheckCircleIcon fontSize="small" />;
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/permissions/employee-overrides')}>
            <FaArrowLeft /> Back to Employee Permission Overrides
          </BackButton>
          {employee && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <EmployeeAvatar employee={employee} size={40} />
              <Title>{employee.fullName} - Permission Overrides</Title>
            </Box>
          )}
        </HeaderLeft>
      </Header>

      <Content>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <FormContainer>
            <FormSection>
              <SectionTitle>Employee Permission Overrides</SectionTitle>

              {employee && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Override specific permissions for <strong>{employee.fullName}</strong>.
                    Grant will explicitly allow the permission, while Deny will explicitly block it.
                  </Typography>
                </Box>
              )}

              {(actionsLoading || resourcesLoading) ? (
                <LoadingSpinner message="Loading permissions data..." />
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell align="center" sx={{ width: '60px' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            #
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Resource
                          </Typography>
                        </TableCell>
                        {actions.map((action) => (
                          <TableCell key={action.id} align="center">
                            <Chip
                              label={action.name}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: action.color,
                                color: action.color,
                                backgroundColor: 'transparent',
                                fontWeight: 500,
                                fontSize: '12px',
                                textTransform: 'capitalize',
                                minWidth: '60px'
                              }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resources.map((resource, index) => (
                        <TableRow key={resource.id}>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                              {index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell component="th" scope="row">
                            <Typography variant="body2" fontWeight="medium">
                              {resource.name}
                            </Typography>
                          </TableCell>
                          {actions.map((action) => (
                            <TableCell key={action.id} align="center">
                              {resourceHasAction(resource, action) ? (
                                (() => {
                                  const permissionCount = getPermissionCount(resource, action);
                                  const override = getOverride(resource, action);
                                  const hasOverrideActive = !!override;

                                  if (!hasOverrideActive && permissionCount > 1) {
                                    return (
                                      <Tooltip title={`${permissionCount} permissions available. Click to select one.`}>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleMultiplePermissionsClick(resource, action)}
                                          color="default"
                                        >
                                          <GroupWorkIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    );
                                  } else {
                                    return (
                                      <Tooltip title={
                                        hasOverrideActive
                                          ? `Override: ${override.overrideType}. Click to remove.`
                                          : `Click to add override for ${action.name} on ${resource.name}`
                                      }>
                                        <IconButton
                                          size="small"
                                          onClick={() => togglePermission(resource, action)}
                                          color={hasOverrideActive ? getOverrideTypeColor(override.overrideType) : 'default'}
                                        >
                                          {hasOverrideActive ? (
                                            getOverrideTypeIcon(override.overrideType)
                                          ) : (
                                            <CancelIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                    );
                                  }
                                })()
                              ) : (
                                <Box sx={{ width: 20, height: 20 }} />
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </FormSection>
          </FormContainer>
        )}
      </Content>

      {/* Permission Override Modal */}
      <Modal
        isOpen={permissionsModalOpen}
        onClose={handlePermissionsModalClose}
        title={selectedResourceAction ? `Override Permission for ${selectedResourceAction.action.name} on ${selectedResourceAction.resource.name}` : 'Override Permission'}
        maxWidth="md"
        onSubmit={handlePermissionSubmit}
        submitText="Add Override"
        submitDisabled={!selectedPermissionId || !selectedOverrideType}
      >
        <Box sx={{ p: 2 }}>
          {selectedResourceAction && (
            <Box>
              {modalAvailablePermissions.length > 1 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Select Permission:
                  </Typography>
                  {modalAvailablePermissions.map((permission, index) => {
                    const isSelected = selectedPermissionId === permission.id;
                    return (
                      <Box
                        key={permission.id || index}
                        onClick={() => handlePermissionSelect(permission.id)}
                        sx={{
                          mb: 2,
                          p: 2,
                          border: isSelected ? '2px solid #2196f3' : '1px solid #e0e0e0',
                          borderRadius: 1,
                          backgroundColor: isSelected ? '#e3f2fd' : '#f9f9f9',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: isSelected ? '#e3f2fd' : '#f0f0f0',
                            borderColor: isSelected ? '#2196f3' : '#ccc'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600, flex: 1 }}>
                            Permission {index + 1}
                          </Typography>
                          {isSelected && (
                            <CheckCircleIcon color="primary" fontSize="small" />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Resource: {selectedResourceAction.resource.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Action: {permission.action?.name || selectedResourceAction.action.name}
                        </Typography>
                        {permission.conditions && Object.keys(permission.conditions).length > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            Conditions: {JSON.stringify(permission.conditions)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </>
              )}

              <Box sx={{ mt: 3 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Override Type</InputLabel>
                  <Select
                    value={selectedOverrideType}
                    onChange={(e) => setSelectedOverrideType(e.target.value)}
                    label="Override Type"
                  >
                    <MenuItem value={PERMISSION_OVERRIDE_TYPES.GRANT}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon color="success" />
                        Grant - Explicitly Allow
                      </Box>
                    </MenuItem>
                    <MenuItem value={PERMISSION_OVERRIDE_TYPES.DENY}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DoNotDisturbOnIcon color="error" />
                        Deny - Explicitly Block
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Reason (Optional)"
                  multiline
                  rows={3}
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Provide a reason for this permission override..."
                  helperText="Optional: Explain why this override is needed"
                />
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
    </Container>
  );
};

export default EmployeeOverrideDetails;
